import {
    type MessageChannel,
    type MessageValue,
    type MessageChannels,
    type MessagesByChannel,
    type ValidationContext
} from './types';
import { isDefaultOnlyFlow } from './flow';
import { stripIndices } from './utils/paths';
import { hasLabelTemplateToken } from './utils/messageTemplates';

export type MessageResolveFn = (channel: MessageChannel, key: string, ctx: ValidationContext) => string;

export type MessageTemplateContext = Pick<ValidationContext, 'path' | 'fieldLabels'>;

/**
 * Returns the last segment of a (potentially nested) validation path.
 *
 * This enables simple label maps like `{ streetAddress: 'Bank Address' }` even when
 * validation paths are nested (e.g. `buyer.address.streetAddress`) or indexed.
 */
function getLeafKeyFromPath(path: string): string {
    const normalized = stripIndices(path);
    const parts = normalized.split('.').filter(Boolean);
    return parts[parts.length - 1] ?? normalized;
}

/**
 * Produces a readable label from an identifier (camelCase/snake_case) when no explicit label is provided.
 *
 * We keep this small and predictable:
 * - `sentence`: "Street address"
 * - `title`: "Street Address"
 */
function humanizeKey(key: string, style: 'sentence' | 'title'): string {
    // camelCase / PascalCase -> words
    const spaced = String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();

    if (!spaced) return '';

    if (style === 'sentence') {
        const lower = spaced.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    // title
    return spaced
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/**
 * Determines the label used for `${label...}` template interpolation.
 *
 * Label resolution precedence:
 * 1) Exact path match: `fieldLabels[ctx.path]`
 * 2) Normalized path match (numeric indices removed): `fieldLabels[stripIndices(ctx.path)]`
 * 3) Leaf key match (last segment): `fieldLabels[leafKey]`
 * 4) Fallback to a humanized leaf key (channel-specific casing)
 */
function resolveTemplateLabel(channel: MessageChannel, ctx: MessageTemplateContext, fallback?: string): string {
    const path = ctx.path ?? '';
    const leafKey = getLeafKeyFromPath(path);
    const labels = ctx.fieldLabels;
    const label = (labels && (labels[path] ?? labels[stripIndices(path)] ?? labels[leafKey])) || '';
    if (label) return label;

    if (typeof fallback === 'string') return fallback;

    // No explicit label provided: fall back to a readable label derived from the leaf key.
    return humanizeKey(leafKey, channel === 'inline' ? 'sentence' : 'title');
}

/**
 * Interpolates label placeholders in message strings.
 *
 * Supported forms:
 * - `${label}`
 * - `${label || 'Street Address'}` (single quotes)
 * - `${label || "Street Address"}` (double quotes)
 *
 * Notes:
 * - We *do not* evaluate arbitrary JS; we only support this narrow `${label}` token.
 * - Fallback defaults live in config for safety and to avoid per-component wiring.
 */
function interpolateMessageTemplate(text: string, channel: MessageChannel, ctx: MessageTemplateContext): string {
    if (!text || typeof text !== 'string') return text;
    // Fast-path: avoid regex work for the common case (no templates).
    if (!hasLabelTemplateToken(text)) return text;

    // Supports:
    // - ${label}
    // - ${label || 'Street Address'}
    // - ${label||"Street Address"}
    //
    // Captures the optional fallback string (single or double quoted) so we can use it when
    // ctx.fieldLabels does not provide a label.
    const token = /\$\{\s*label\s*(?:\|\|\s*(?:'([^']*)'|"([^"]*)"))?\s*\}/g;
    return text.replace(token, (_full, singleQuotedFallback: string | undefined, doubleQuotedFallback: string | undefined) => {
        const fallback = singleQuotedFallback ?? doubleQuotedFallback;
        return resolveTemplateLabel(channel, ctx, fallback);
    });
}

/**
 * Public wrapper for label-only message template interpolation.
 *
 * Useful for config transforms that want to pre-bake `${label...}` placeholders
 * without requiring a full ValidationContext.
 */
export function interpolateLabelTemplate(text: string, channel: MessageChannel, ctx: MessageTemplateContext): string {
    return interpolateMessageTemplate(text, channel, ctx);
}

function resolveValue(
    value: MessageValue | undefined,
    channel: MessageChannel,
    ctx: ValidationContext,
    resolveFn?: MessageResolveFn
): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return interpolateMessageTemplate(value, channel, ctx);
    const key = (value as { key: string }).key;
    if (!key) return undefined;
    const resolved = resolveFn ? resolveFn(channel, key, ctx) : key;
    return interpolateMessageTemplate(resolved, channel, ctx);
}

// Normalize rule messages (single or per-channel) into per-channel strings
export function normalizeMessages(
    messages: MessageValue | MessageChannels | undefined,
    ctx: ValidationContext,
    resolveFn?: MessageResolveFn
): MessagesByChannel {
    const out: MessagesByChannel = {};
    if (!messages) return out;

    // Single value: apply to both channels
    if (typeof messages === 'string' || ('key' in (messages as any) && typeof (messages as any).key === 'string')) {
        const inline = resolveValue(messages as MessageValue, 'inline', ctx, resolveFn);
        let gcalert = resolveValue(messages as MessageValue, 'gcalert', ctx, resolveFn);
        if (inline) out.inline = inline;
        if (gcalert) {
            const cfgPrefix = ctx.errorPrefix;
            if (!isDefaultOnlyFlow(ctx.flow) && cfgPrefix) gcalert = `${cfgPrefix} ${gcalert}`;
            out.gcalert = gcalert;
        }
        return out;
    }

    const chans = messages as MessageChannels;
    const inline = resolveValue(chans.inline, 'inline', ctx, resolveFn);
    let gcalert = resolveValue(chans.gcalert, 'gcalert', ctx, resolveFn);
    if (inline) out.inline = inline;
    if (gcalert) {
        const cfgPrefix = ctx.errorPrefix;
        if (!isDefaultOnlyFlow(ctx.flow) && cfgPrefix) gcalert = `${cfgPrefix} ${gcalert}`;
        out.gcalert = gcalert;
    }
    return out;
}

// Convenience: get the resolved message for a single channel from a rule
import type { ValidationEngineRule } from './types';

export function getMessageForChannel(
    rule: ValidationEngineRule,
    channel: MessageChannel,
    ctx: ValidationContext,
    resolveFn?: MessageResolveFn
): string | undefined {
    const normalized = normalizeMessages(rule.messages, ctx, resolveFn);
    return normalized[channel];
}
